import type { ProfileResource } from '@medplum/core'
import type {
  Patient,
  Practitioner,
  RelatedPerson,
  Resource,
} from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { isOfType } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import { startCase } from 'lodash-es'
import { type JSX, useState } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ResourceForm } from '#src/components/medplum/ResourceForm.tsx'
import { useFhirSaveMutation } from '#src/hooks/useFhirSaveMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export type EditableProfileField =
  | 'telecom'
  | 'communication'
  | 'address'
  | 'birthDate'
  | 'name'
  | 'gender'
  | 'photo'

export interface EditProfileFieldDialogProps {
  profile: ProfileResource
  field: EditableProfileField
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditProfileFieldDialog({
  profile,
  field,
  open,
  onOpenChange,
}: EditProfileFieldDialogProps): JSX.Element {
  const savePatientMutation = useFhirSaveMutation(profile.resourceType)

  const handleSubmit = (resource: Resource): void => {
    invariant(
      isOfType(resource, profile.resourceType),
      'Resource type mismatch'
    )
    savePatientMutation.mutate(resource)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader asChild>
          <DialogTitle>
            Edit {startCase(field.replace(/([A-Z])/g, ' $1').trim())}
          </DialogTitle>
        </DialogHeader>
        <DialogContent>
          {/* We split by resource type here to satisfy ResourceForm's types. */}
          {isOfType(profile, 'Patient') && (
            <ResourceForm<Patient>
              defaultValue={profile}
              onSubmit={handleSubmit}
              visibleFields={[field]}
            />
          )}
          {isOfType(profile, 'RelatedPerson') && (
            <ResourceForm<RelatedPerson>
              defaultValue={profile}
              onSubmit={handleSubmit}
              visibleFields={[field]}
            />
          )}
          {isOfType(profile, 'Practitioner') && (
            <ResourceForm<Practitioner>
              defaultValue={profile}
              onSubmit={handleSubmit}
              visibleFields={[field]}
            />
          )}
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}

export interface EditButtonProps {
  profile: ProfileResource
  field: EditableProfileField
  className?: string
}

export function EditButton({
  profile,
  field,
  className,
}: EditButtonProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const [open, setOpen] = useState(false)

  if (isReadOnly) {
    return null
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={className}
        onClick={() => setOpen(true)}
      >
        <IconPencil className="size-4" />
      </Button>
      <EditProfileFieldDialog
        profile={profile}
        field={field}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}
